/**
 * /tools/celebration-system?clientId=X — STP-043.
 *
 * The client locks in what to celebrate at each pre-defined fulfillment
 * threshold + streak milestone, plus optional custom milestones.
 * Persists as versioned `client_documents` doc_type=`celebration`.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

const TRIGGER_KEYS = [
  'fulfillment_80_seven_days',
  'fulfillment_90_seven_days',
  'streak_21_days',
  'weekly_focus_hit',
  'three_weeks_focus_hit',
] as const;

type TriggerKey = (typeof TRIGGER_KEYS)[number];

interface CustomRow {
  triggerLabel: string;
  celebration: string;
}

interface FormState {
  byKey: Record<TriggerKey, string>;
  custom: CustomRow[];
  reflection: string;
}

const EMPTY_BY_KEY: Record<TriggerKey, string> = {
  fulfillment_80_seven_days: '',
  fulfillment_90_seven_days: '',
  streak_21_days: '',
  weekly_focus_hit: '',
  three_weeks_focus_hit: '',
};

const DEFAULT_FORM: FormState = {
  byKey: { ...EMPTY_BY_KEY },
  custom: [{ triggerLabel: '', celebration: '' }],
  reflection: '',
};

export function CelebrationSystemPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'celebration-system');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM, byKey: { ...EMPTY_BY_KEY }, custom: [] };
          if (Array.isArray(p.milestones)) {
            for (const raw of p.milestones) {
              const m = raw as Record<string, unknown>;
              const key = m.triggerKey;
              const cel = m.celebration;
              if (
                typeof key === 'string' &&
                typeof cel === 'string' &&
                (TRIGGER_KEYS as readonly string[]).includes(key)
              ) {
                next.byKey[key as TriggerKey] = cel;
              }
            }
          }
          if (Array.isArray(p.customMilestones) && p.customMilestones.length > 0) {
            next.custom = p.customMilestones.map((raw) => {
              const m = raw as Record<string, unknown>;
              return {
                triggerLabel: typeof m.triggerLabel === 'string' ? m.triggerLabel : '',
                celebration: typeof m.celebration === 'string' ? m.celebration : '',
              };
            });
          } else {
            next.custom = [{ triggerLabel: '', celebration: '' }];
          }
          if (typeof p.reflection === 'string') next.reflection = p.reflection;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const filledMilestones = TRIGGER_KEYS.filter(
    (key) => form.byKey[key].trim().length > 0,
  );
  const filledCustom = form.custom.filter(
    (c) => c.triggerLabel.trim().length > 0 && c.celebration.trim().length > 0,
  );
  const canSubmit =
    !submitting &&
    hydrated &&
    (filledMilestones.length > 0 || filledCustom.length > 0);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.celebrationSystem.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      milestones: filledMilestones.map((triggerKey) => ({
        triggerKey,
        celebration: form.byKey[triggerKey].trim(),
      })),
      ...(filledCustom.length > 0
        ? {
            customMilestones: filledCustom.map((c) => ({
              triggerLabel: c.triggerLabel.trim(),
              celebration: c.celebration.trim(),
            })),
          }
        : {}),
      ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'celebration-system',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.celebrationSystem.errors.generic'));
      } else {
        setSubmitError(t('tools.celebrationSystem.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="celebration-system-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.celebrationSystem.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.celebrationSystem.errors.missingClient')}
        </p>
      </section>
    );
  }

  const setKey = (key: TriggerKey, value: string): void => {
    setForm((prev) => ({ ...prev, byKey: { ...prev.byKey, [key]: value } }));
  };
  const setCustom = (idx: number, key: keyof CustomRow, value: string): void => {
    setForm((prev) => ({
      ...prev,
      custom: prev.custom.map((c, i) => (i === idx ? { ...c, [key]: value } : c)),
    }));
  };
  const addCustom = (): void => {
    setForm((prev) =>
      prev.custom.length >= 20
        ? prev
        : { ...prev, custom: [...prev.custom, { triggerLabel: '', celebration: '' }] },
    );
  };
  const removeCustom = (idx: number): void => {
    setForm((prev) =>
      prev.custom.length <= 1
        ? prev
        : { ...prev, custom: prev.custom.filter((_, i) => i !== idx) },
    );
  };

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.celebrationSystem.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.celebrationSystem.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.celebrationSystem.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.celebrationSystem.milestones.title')}
          </h2>
          <p className="mt-s2 text-gray-2 italic">{t('tools.celebrationSystem.milestones.hint')}</p>
          <ol className="mt-s3 space-y-s3">
            {TRIGGER_KEYS.map((key) => (
              <li key={key} className="rounded-md border border-bone-3 bg-bone p-s3 space-y-s2">
                <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
                  {t(`tools.celebrationSystem.triggers.${key}`)}
                </p>
                <input
                  type="text"
                  value={form.byKey[key]}
                  onChange={(e) => setKey(key, e.target.value)}
                  placeholder={t('tools.celebrationSystem.placeholders.celebration')}
                  className="input-field w-full"
                  data-testid={`celebration-${key}`}
                />
              </li>
            ))}
          </ol>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.celebrationSystem.custom.title')}
            <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
              {t('tools.celebrationSystem.optional')}
            </span>
          </h2>
          <ol className="mt-s3 space-y-s3">
            {form.custom.map((c, idx) => (
              <li
                key={idx}
                className="grid grid-cols-12 gap-s2 items-start"
                data-testid={`celebration-custom-${idx}`}
              >
                <input
                  type="text"
                  value={c.triggerLabel}
                  onChange={(e) => setCustom(idx, 'triggerLabel', e.target.value)}
                  placeholder={t('tools.celebrationSystem.placeholders.customTrigger')}
                  className="input-field col-span-5"
                  data-testid={`celebration-custom-label-${idx}`}
                />
                <input
                  type="text"
                  value={c.celebration}
                  onChange={(e) => setCustom(idx, 'celebration', e.target.value)}
                  placeholder={t('tools.celebrationSystem.placeholders.celebration')}
                  className="input-field col-span-6"
                  data-testid={`celebration-custom-celebration-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeCustom(idx)}
                  disabled={form.custom.length <= 1}
                  className="col-span-1 btn-ghost text-sm"
                  data-testid={`celebration-custom-remove-${idx}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ol>
          <button
            type="button"
            onClick={addCustom}
            disabled={form.custom.length >= 20}
            className="btn-ghost mt-s3"
            data-testid="celebration-custom-add"
          >
            + {t('tools.celebrationSystem.custom.add')}
          </button>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.celebrationSystem.fields.reflection')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.celebrationSystem.optional')}
              </span>
            </span>
            <textarea
              value={form.reflection}
              onChange={(e) => setForm((prev) => ({ ...prev, reflection: e.target.value }))}
              className="input-field mt-s2 min-h-20"
              data-testid="celebration-reflection"
              rows={3}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="celebration-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="celebration-saved-at">
            ✓ {t('tools.celebrationSystem.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <FinalizeWarning />

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.celebrationSystem.submitting') : t('tools.celebrationSystem.submit')}
        </button>
      </form>
    </section>
  );
}
